import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared-ui/Button';
import { submitText, isQuotaExceededError } from '@/core/services/noteProcessing';
import { QuickTextImageStrip } from './QuickTextImageStrip';
import { useComposerImages } from '../hooks/useComposerImages';

export function QuickText() {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const composerImages = useComposerImages();

  const submit = async () => {
    const t = text.trim();
    if (!t && composerImages.items.length === 0) return;
    setBusy(true);
    setErr(null);
    try {
      const submitStartedAt = Date.now();
      const { imageUrls, uploadMetrics } = await composerImages.waitForAllUploads();
      const clientMetrics = uploadMetrics
        ? {
            ...uploadMetrics,
            client_pre_request_ms: Math.max(0, Date.now() - submitStartedAt),
          }
        : undefined;
      const { taskId } = await submitText({
        text: t,
        imageUrls,
        clientMetrics,
      });
      // optimistic clear + navigate as soon as backend acknowledges the task —
      // the driver keeps streaming in the background even if we leave.
      setText('');
      composerImages.clearAll();
      navigate(`/tasks/${taskId}`);
    } catch (e) {
      if (isQuotaExceededError(e)) {
        navigate('/membership?reason=quota');
        return;
      }
      setErr((e as Error).message ?? '提交失败');
    } finally {
      setBusy(false);
    }
  };

  const helperText = busy
    ? '已提交，正在跳转到处理页…'
    : composerImages.hasPendingUploads
      ? `图片预上传中 ${Math.round(composerImages.aggregateUploadProgress * 100)}%`
      : composerImages.items.length > 0
        ? '图片会先直传 OSS，提交后与文字一起触发同一条 LLM 流程。'
        : '可直接输入文字，也支持纯图片提交。';

  return (
    <div className="rounded-2xl border border-border bg-bg-elevated p-3">
      <textarea
        className="w-full resize-none bg-transparent text-sm outline-none placeholder:text-fg-subtle"
        placeholder="记一笔，贴几张图，或直接丢一组图片进来…"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={busy}
      />
      <div className="mt-3">
        <QuickTextImageStrip
          items={composerImages.items}
          remaining={composerImages.remaining}
          disabled={busy}
          error={composerImages.error}
          onAdd={composerImages.addFiles}
          onRemove={composerImages.remove}
        />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="pr-3 text-xs text-fg-subtle">
          {err ? <span className="text-danger">{err}</span> : helperText}
        </div>
        <Button
          onClick={() => void submit()}
          loading={busy}
          disabled={!text.trim() && composerImages.items.length === 0}
        >
          提交
        </Button>
      </div>
    </div>
  );
}
